import { useState, useEffect, useCallback } from 'react';
import type { SandboxPost, FeedFilter } from '../types/sandbox';
import { supabase } from '../lib/supabase';
import { subscribeToFeed } from '../lib/realtime';
import { getMockPosts } from '../lib/mock-data';

export function useFeed(filter?: FeedFilter) {
  const [posts, setPosts] = useState<SandboxPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (supabase) {
        let query = supabase
          .from('sandbox_posts')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);

        if (filter?.groupSlug) {
          query = query.eq('group_slug', filter.groupSlug);
        }
        if (filter?.postType) {
          query = query.eq('post_type', filter.postType);
        }
        if (filter?.authorType) {
          query = query.eq('author_type', filter.authorType);
        }

        const { data, error: dbError } = await query;
        if (dbError) throw new Error(dbError.message);
        setPosts((data || []) as SandboxPost[]);
      } else {
        // Mock mode
        const mockPosts = getMockPosts({
          groupSlug: filter?.groupSlug,
        });
        setPosts(mockPosts);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [filter?.groupSlug, filter?.postType, filter?.authorType]);

  // Initial load
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Realtime subscription
  useEffect(() => {
    const unsubscribe = subscribeToFeed((newPost) => {
      // Only add if it matches our filter
      if (filter?.groupSlug && newPost.group_slug !== filter.groupSlug) return;
      if (filter?.postType && newPost.post_type !== filter.postType) return;
      if (filter?.authorType && newPost.author_type !== filter.authorType) return;

      setPosts(prev => [newPost, ...prev]);
    });

    return () => {
      unsubscribe?.();
    };
  }, [filter?.groupSlug, filter?.postType, filter?.authorType]);

  const addPost = useCallback(async (post: Partial<SandboxPost>) => {
    if (supabase) {
      const { data, error: dbError } = await supabase
        .from('sandbox_posts')
        .insert(post)
        .select()
        .single();
      if (dbError) throw new Error(dbError.message);
      // Realtime will handle adding to feed
      return data as SandboxPost;
    }

    // Mock mode - add locally
    const mockPost: SandboxPost = {
      id: crypto.randomUUID(),
      author_type: post.author_type || 'operator',
      author_did: post.author_did || '',
      post_type: post.post_type || 'text',
      content: post.content || '',
      media_url: null,
      group_slug: post.group_slug || null,
      parent_id: null,
      reactions: { clap: 0, fire: 0, brain: 0, rocket: 0, sym: 0 },
      metadata: {},
      created_at: new Date().toISOString(),
    };
    setPosts(prev => [mockPost, ...prev]);
    return mockPost;
  }, []);

  const react = useCallback(async (postId: string, reactionType: string) => {
    if (supabase) {
      // Increment reaction count via RPC or direct update
      const post = posts.find(p => p.id === postId);
      if (!post) return;
      const newReactions = { ...post.reactions };
      newReactions[reactionType as keyof typeof newReactions] = (newReactions[reactionType as keyof typeof newReactions] || 0) + 1;
      await supabase
        .from('sandbox_posts')
        .update({ reactions: newReactions })
        .eq('id', postId);
    }

    // Optimistic update
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const reactions = { ...p.reactions };
      reactions[reactionType as keyof typeof reactions] = (reactions[reactionType as keyof typeof reactions] || 0) + 1;
      return { ...p, reactions };
    }));
  }, [posts]);

  return { posts, loading, error, refresh: fetchPosts, addPost, react };
}
